import { AxiosResponse } from 'axios';
import { Notification } from 'components/UI';
import { reject } from 'es-toolkit/compat';
import { IAttachedFile } from 'interfaces';
import { action, observable } from 'mobx';
import { RootStore } from 'stores/RootStore';
import omsApi, { camelizeKeys } from 'utils/axios';

interface AttachedFilesResponse {
  payload: {
    attached_files: IAttachedFile[];
  };
}
interface UploadFilesResponse {
  payload: {
    attached_file: IAttachedFile;
  };
}

class AttachedFilesStore {
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @observable attachments: IAttachedFile[] = [];

  @observable fetchStatus: Record<string, { isFetched: boolean; isFetching: boolean; fetchError: boolean }> = {};

  @observable uploadStatus: Record<string, { isFinished: boolean; isProcessing: boolean; errored: boolean }> = {};

  @action fetch = async (url: string, context: string = 'default') => {
    this.fetchStatus[context] = { isFetched: false, isFetching: true, fetchError: false };

    try {
      const response = await omsApi.get<AttachedFilesResponse>(url);
      this.onFetchSuccess(response, context);

      return response;
    } catch (err: unknown) {
      this.onFetchError(context);
      Notification.Error('Failed to fetch attachments');

      return err;
    }
  };

  @action onFetchSuccess = (response: AxiosResponse<AttachedFilesResponse>, context: string): void => {
    const { payload } = response.data;

    this.fetchStatus[context] = { isFetched: true, isFetching: false, fetchError: false };
    this.attachments = camelizeKeys(payload?.attached_files || []).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  };

  @action onFetchError = (context: string): void => {
    this.fetchStatus[context] = { isFetched: false, isFetching: false, fetchError: true };
  };

  @action upload = async (url: string, formData: FormData, context: string = 'default') => {
    this.uploadStatus[context] = { isFinished: false, isProcessing: true, errored: false };

    try {
      const response = await omsApi.post<UploadFilesResponse>(url, formData);

      this.finishUpload(context);

      const attachment = response.data.payload.attached_file;
      this.addAttachment(camelizeKeys(attachment));

      Notification.Success(`Attachment ${attachment.name} added`);

      this.rootStore.activityRecordStore.refetch();
    } catch (err) {
      console.error(err);
      this.finishUpload(context, true);
      Notification.Error('Failed to upload attachment');
    }
  };

  @action delete = async (url: string, id: number) => {
    try {
      await omsApi.delete(url);

      this.deleteAttachment(id);

      Notification.Success('Attachment has been deleted');

      this.rootStore.activityRecordStore.refetch();
    } catch (err) {
      console.error(err);
      Notification.Error('Failed to delete attachment');
    }
  };

  @action addAttachment = (file: IAttachedFile): void => {
    this.attachments.push(file);
  };

  @action deleteAttachment = (id: number): void => {
    this.attachments = reject<IAttachedFile>(this.attachments, { id });
  };

  @action finishUpload = (context: string, errored = false): void => {
    this.uploadStatus[context] = { isFinished: true, isProcessing: false, errored };
  };
}

export default AttachedFilesStore;
